import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@aix/ui"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

dayjs.extend(relativeTime)

const formatDistanceToNow = (date: Date, _opts?: { addSuffix?: boolean }): string => dayjs(date).fromNow()
import { BarChart, CheckCircle, Clock, Eye, Send, XCircle } from "lucide-react"
import React, { useState } from "react"
import { trpc } from "~/lib/trpc"

export const CampaignList: React.FC = () => {
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null)

  const { data: campaigns, refetch } = trpc.emailMarketing.listCampaigns.useQuery({
    limit: 50,
  })

  const { data: campaignStats } = trpc.emailMarketing.getCampaignStats.useQuery(
    { campaignId: selectedCampaign },
    { enabled: !!selectedCampaign },
  )

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { label: "Draft", variant: "secondary" as const },
      scheduled: { label: "Scheduled", variant: "outline" as const },
      sending: { label: "Sending", variant: "default" as const },
      sent: { label: "Sent", variant: "default" as const },
      paused: { label: "Paused", variant: "secondary" as const },
      failed: { label: "Failed", variant: "destructive" as const },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "draft":
        return <Clock className="w-4 h-4 text-muted-foreground" />
      case "sending":
        return <Send className="w-4 h-4 text-primary" />
      case "sent":
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case "failed":
        return <XCircle className="w-4 h-4 text-destructive" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Email Campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {campaigns?.map((campaign) => (
              <div
                key={campaign.id}
                className="p-4 border border-border rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer"
                onClick={() => setSelectedCampaign(campaign.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(campaign.status)}
                      <h3 className="font-semibold text-foreground">{campaign.name}</h3>
                      {getStatusBadge(campaign.status)}
                    </div>

                    <p className="text-sm text-muted-foreground mb-2">Subject: {campaign.subject}</p>

                    <div className="flex items-center gap-6 text-xs text-muted-foreground">
                      <span>To: {campaign.totalRecipients} recipients</span>
                      {campaign.sentCount > 0 && <span>Sent: {campaign.sentCount}</span>}
                      {campaign.openedCount > 0 && <span>Opens: {campaign.openedCount}</span>}
                      {campaign.clickedCount > 0 && <span>Clicks: {campaign.clickedCount}</span>}
                    </div>

                    <div className="mt-2 text-xs text-muted-foreground">
                      Created {formatDistanceToNow(new Date(campaign.createdAt), { addSuffix: true })}
                      {campaign.completedAt && (
                        <> • Completed {formatDistanceToNow(new Date(campaign.completedAt), { addSuffix: true })}</>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedCampaign(campaign.id)
                      }}
                    >
                      <BarChart className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {(!campaigns || campaigns.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                No campaigns yet. Create your first campaign to get started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedCampaign && campaignStats && (
        <Card>
          <CardHeader>
            <CardTitle>Campaign Analytics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <h3 className="font-semibold">{campaignStats.campaign.name}</h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-sm text-muted-foreground">Sent</p>
                  <p className="text-2xl font-bold">{campaignStats.stats.totalSent}</p>
                </div>

                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-sm text-muted-foreground">Delivered</p>
                  <p className="text-2xl font-bold">{campaignStats.stats.delivered}</p>
                </div>

                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-sm text-muted-foreground">Open Rate</p>
                  <p className="text-2xl font-bold">{campaignStats.stats.openRate.toFixed(1)}%</p>
                </div>

                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-sm text-muted-foreground">Click Rate</p>
                  <p className="text-2xl font-bold">{campaignStats.stats.clickRate.toFixed(1)}%</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="text-sm text-muted-foreground">Bounced</p>
                  <p className="text-lg font-semibold text-destructive">{campaignStats.stats.bounced}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Complained</p>
                  <p className="text-lg font-semibold text-destructive">{campaignStats.stats.complained}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Cost</p>
                  <p className="text-lg font-semibold">
                    ${((campaignStats.campaign.actualCost || 0) / 100).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
